import i18n from '@/i18n';
import { toStoreTime } from '@/lib/store-time';

/**
 * Formatting helpers, in one place.
 *
 * The old codebase reached for moment-timezone in ~40 files and wrote
 * `moment(`${value}+0000`).tz(timezone).format('MMM Do YYYY, h:mm:ss a')`
 * inline each time — a 70 kB dependency, a hand-appended UTC marker, and a
 * format string that differed slightly wherever it was copied. `Intl` does all
 * of it natively and follows the active language for free.
 */

/**
 * API timestamps are UTC but arrive without a zone marker.
 *
 * Exported as well as used here: anything that groups rows by day needs the
 * instant itself, and re-parsing a formatted string to get it back is how you
 * end up with two slightly different notions of "yesterday".
 */
export function parseApiDate(
  value: string | number | Date | null | undefined
): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);

  // Append 'Z' when the string carries no offset, so it is not read as local.
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const date = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function locale() {
  return i18n.resolvedLanguage === 'es' ? 'es-PR' : 'en-US';
}

export function formatDate(value: Parameters<typeof parseApiDate>[0]): string {
  const date = parseApiDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(locale(), {
    dateStyle: 'medium',
  }).format(date);
}

/**
 * A calendar date — `yyyy-MM-dd` with no instant behind it.
 *
 * `formatDate` is for timestamps: it appends `Z` to a zoneless value and
 * renders it in the reader's zone, which is right for "when did this order
 * dispatch" and wrong for "which day is this". West of UTC the two disagree:
 * `2026-09-07` read as midnight UTC is the evening of the 6th locally, so
 * `formatDate` printed the day before the one asked for. Use this wherever the
 * string is a day rather than a moment.
 */
export function formatCalendarDate(value: string | null | undefined): string {
  if (!value) return '—';
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '—';
  return new Intl.DateTimeFormat(locale(), { dateStyle: 'medium' }).format(
    // Local midnight, so no zone conversion can move it off the named day.
    new Date(year, month - 1, day)
  );
}

export function formatDateTime(value: Parameters<typeof parseApiDate>[0]): string {
  const date = parseApiDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(locale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/**
 * A timestamp on the store's clock rather than the reader's — for reports about
 * the store's own day, where 5:12 PM should read 5:12 PM to whoever opens it.
 *
 * `toStoreTime` shifts the instant so its UTC fields hold the store's wall
 * clock, so it is formatted in UTC here; formatting it in the reader's zone
 * would apply a second offset on top of the first.
 */
export function formatStoreDateTime(
  value: string | null | undefined,
  part: 'date' | 'time' | 'both' = 'both'
): string {
  const date = toStoreTime(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(locale(), {
    ...(part !== 'time' && { dateStyle: 'medium' }),
    ...(part !== 'date' && { timeStyle: 'short' }),
    timeZone: 'UTC',
  }).format(date);
}

/**
 * "Mon, Oct 5" — a day named the way a rota names it.
 *
 * Takes a date whose UTC fields hold the day to print, which is what
 * `toStoreTime` produces. The year only appears when it is not this one: on a
 * timesheet it is the weekday that gets read, and the year is noise until the
 * range crosses January.
 */
function weekdayLabel(date: Date, withTime = false): string {
  return new Intl.DateTimeFormat(locale(), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.getUTCFullYear() !== new Date().getFullYear() && {
      year: 'numeric',
    }),
    ...(withTime && { hour: 'numeric', minute: '2-digit' }),
    timeZone: 'UTC',
  }).format(date);
}

function clockLabel(date: Date): string {
  return new Intl.DateTimeFormat(locale(), {
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(date);
}

/** A timestamp's day on the store's clock, with its weekday. */
export function formatStoreDay(value: string | null | undefined): string {
  const date = toStoreTime(value);
  return date ? weekdayLabel(date) : '—';
}

/** "Mon, Oct 5, 8:00 AM" — a store-clock moment, with its weekday. */
export function formatStoreStamp(value: string | null | undefined): string {
  const date = toStoreTime(value);
  return date ? weekdayLabel(date, true) : '—';
}

/** A `yyyy-MM-dd` day with its weekday. No instant behind it, so no shift. */
export function formatCalendarDay(value: string | null | undefined): string {
  const date = value ? new Date(`${value.slice(0, 10)}T00:00:00.000Z`) : null;
  return date && !Number.isNaN(date.getTime()) ? weekdayLabel(date) : '—';
}

/**
 * A shift, start to end, on the store's clock.
 *
 * The day is said once when the shift ends on the day it started, which is
 * nearly always — "Mon, Oct 5 · 8:00 AM – 4:30 PM" — and twice only when it
 * runs past midnight. `withDay: false` drops the opening day for a list that
 * already sits under it.
 */
export function formatStoreShift(
  start: string | null | undefined,
  end: string | null | undefined,
  /** What to print in place of the end of a shift that is still running. */
  open: string,
  withDay = true
): string {
  const from = toStoreTime(start);
  if (!from) return '—';
  const to = toStoreTime(end);

  const opening = withDay
    ? `${weekdayLabel(from)} · ${clockLabel(from)}`
    : clockLabel(from);
  if (!to) return `${opening} → ${open}`;

  const sameDay =
    from.toISOString().slice(0, 10) === to.toISOString().slice(0, 10);
  return sameDay
    ? `${opening} – ${clockLabel(to)}`
    : `${opening} → ${weekdayLabel(to, true)}`;
}

/** "October 2026", from a `yyyy-MM`. */
export function formatMonth(value: string): string {
  return new Intl.DateTimeFormat(locale(), {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}-01T00:00:00.000Z`));
}

/** Short weekday names, Monday first. */
export function weekdayNames(): string[] {
  const format = new Intl.DateTimeFormat(locale(), {
    weekday: 'short',
    timeZone: 'UTC',
  });
  // 2024-01-01 was a Monday.
  return Array.from({ length: 7 }, (_, index) =>
    format.format(new Date(Date.UTC(2024, 0, 1 + index)))
  );
}

/** Just the clock time. For a list already grouped under its day. */
export function formatTime(value: Parameters<typeof parseApiDate>[0]): string {
  const date = parseApiDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(locale(), { timeStyle: 'short' }).format(date);
}

/** "3 months ago" — for a "user since" line, where the exact minute is noise. */
export function formatRelative(value: Parameters<typeof parseApiDate>[0]): string {
  const date = parseApiDate(value);
  if (!date) return '—';

  const seconds = (date.getTime() - Date.now()) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];

  const formatter = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) {
      return formatter.format(Math.round(seconds / size), unit);
    }
  }
  return formatter.format(Math.round(seconds), 'second');
}

/** (787) 555-0123 — falls back to the raw value rather than the old `null`. */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '');
  const match = digits.match(/^1?(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : value;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(locale()).format(value);
}

export function formatCurrency(
  value: number | null | undefined,
  currency = 'USD'
): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(locale(), { style: 'currency', currency }).format(
    value
  );
}

/** Two-letter monogram for an avatar fallback. */
export function initials(first?: string | null, last?: string | null): string {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase() || '?';
}

/**
 * Percentages stored as fractions.
 *
 * Two different tables do this — `PosDiscount.DiscountValue` and
 * `LoyaltyReward.Value` — and both are applied as `total * value` with no
 * divide, so 0.1 is ten per cent. Everything a person reads or types is a
 * percentage, so the conversion happens at the edges and nowhere else.
 *
 * Rounded on the way out because 0.075 * 100 is 7.500000000000001 in binary
 * floating point, and a discount that renders as 7.500000000000001% is its own
 * kind of wrong. Four decimal places of fraction, two of percentage.
 */
export function toPercent(fraction: number): number {
  return Math.round(fraction * 10000) / 100;
}

export function fromPercent(percent: number): number {
  return Math.round(percent * 100) / 10000;
}
